import { supabase } from '@/lib/supabase';
import { getRequirementIdForBooking } from './delegateService';

/**
 * Fetches the compliance_requirements record for a booking.
 * Tries multiple matching strategies:
 * 1. Direct requirement_id on booking
 * 2. course_id matching course_link_id
 * 3. Course name fuzzy matching
 *
 * Returns { id, course_link_id, course_link_type, name } or null.
 */
export async function getRequirementDetails(booking) {
  // Strategy 1 & 2: Try direct ID or course_link_id match
  const requirementId = await getRequirementIdForBooking(booking);
  if (requirementId) {
    const { data } = await supabase
      .from('compliance_requirements')
      .select('id, course_link_id, course_link_type, name')
      .eq('id', requirementId)
      .maybeSingle();

    if (data) return data;
  }

  // Strategy 3: Try matching by course name within the project
  if (booking.course_name && booking.project_id) {
    const { data: requirements } = await supabase
      .from('compliance_requirements')
      .select('id, course_link_id, course_link_type, name')
      .eq('project_id', booking.project_id);

    if (requirements && requirements.length > 0) {
      const courseNameLower = booking.course_name.toLowerCase().trim();

      // Find exact match first
      let match = requirements.find(r =>
        r.name?.toLowerCase().trim() === courseNameLower
      );

      // Then try partial match (course name contains requirement name or vice versa)
      if (!match) {
        match = requirements.find(r => {
          const reqNameLower = r.name?.toLowerCase().trim() || '';
          return courseNameLower.includes(reqNameLower) || reqNameLower.includes(courseNameLower);
        });
      }

      if (match) return match;
    }
  }

  return null;
}

/**
 * Fetches compliance status for space members.
 * Checks both compliance_assignments (for assigned members) and user_qualifications (for all members).
 * This allows us to recommend people who aren't assigned but also don't have a valid certificate.
 *
 * @param {string} projectId
 * @param {string} requirementId
 * @param {string|null} courseLinkId
 * @param {string[]} userIds - user_profiles.id values
 * @returns {Promise<Map<string, { status, expiry_date, quote_id, hasAssignment, hasQualification }>>}
 */
export async function fetchSpaceMemberCompliance(projectId, requirementId, courseLinkId, userIds) {
  const map = new Map();
  if (!projectId || !userIds?.length) return map;

  // Initialize all users as having no data
  for (const userId of userIds) {
    map.set(userId, {
      status: null,
      expiry_date: null,
      quote_id: null,
      hasAssignment: false,
      hasQualification: false
    });
  }

  // Fetch compliance_assignments if we have a requirement
  if (requirementId) {
    const { data: assignments, error: assignError } = await supabase
      .from('compliance_assignments')
      .select('team_member_id, status, expiry_date, quote_id, assigned')
      .eq('project_id', projectId)
      .eq('requirement_id', requirementId)
      .in('team_member_id', userIds);

    if (assignError) {
      console.warn('fetchSpaceMemberCompliance assignments error:', assignError);
    } else {
      for (const row of assignments || []) {
        const existing = map.get(row.team_member_id) || {};
        map.set(row.team_member_id, {
          ...existing,
          status: row.status,
          expiry_date: row.expiry_date,
          quote_id: row.quote_id,
          hasAssignment: row.assigned === true
        });
      }
    }
  }

  // Check user_qualifications for actual qualification status
  // This is the source of truth for whether someone has a valid/expired/missing certificate
  const qualQueries = [];
  if (requirementId) {
    qualQueries.push(
      supabase
        .from('user_qualifications')
        .select('user_id, status, expiry_date')
        .eq('requirement_id', requirementId)
        .in('user_id', userIds)
    );
  }
  if (courseLinkId) {
    qualQueries.push(
      supabase
        .from('user_qualifications')
        .select('user_id, status, expiry_date')
        .eq('course_link_id', courseLinkId.toString())
        .in('user_id', userIds)
    );
  }

  if (qualQueries.length > 0) {
    const results = await Promise.all(qualQueries);
    for (const { data: quals, error: qualError } of results) {
      if (qualError) {
        console.warn('fetchSpaceMemberCompliance qualifications error:', qualError);
        continue;
      }
      for (const qual of quals || []) {
        const existing = map.get(qual.user_id);
        if (existing) {
          // Only treat as a completed qualification if:
          // 1. They have an expiry_date (implies they got a cert), OR
          // 2. Status explicitly indicates completion
          const hasCompletedQualification = qual.expiry_date ||
            ['completed', 'active', 'valid'].includes(qual.status);

          if (hasCompletedQualification) {
            existing.hasQualification = true;
            existing.status = 'completed';
            existing.expiry_date = qual.expiry_date;
          }
        }
      }
    }
  }

  return map;
}

/**
 * Fetches compliance status for company members from user_qualifications.
 * Tries both requirement_id and course_link_id, preferring requirement_id match.
 * @param {string|null} requirementId
 * @param {string|null} courseLinkId
 * @param {string[]} userIds
 * @returns {Promise<Map<string, { status, expiry_date, renewal_status, quote_id }>>}
 */
export async function fetchCompanyMemberCompliance(requirementId, courseLinkId, userIds) {
  const map = new Map();
  if (!userIds?.length) return map;
  if (!requirementId && !courseLinkId) return map;

  const queries = [];

  if (requirementId) {
    queries.push(
      supabase
        .from('user_qualifications')
        .select('user_id, status, expiry_date, renewal_status, quote_id')
        .eq('requirement_id', requirementId)
        .in('user_id', userIds)
    );
  }

  if (courseLinkId) {
    queries.push(
      supabase
        .from('user_qualifications')
        .select('user_id, status, expiry_date, renewal_status, quote_id')
        .eq('course_link_id', courseLinkId.toString())
        .in('user_id', userIds)
    );
  }

  const results = await Promise.all(queries);

  // Process course_link results first, then requirement_id results override
  for (let i = results.length - 1; i >= 0; i--) {
    const { data, error } = results[i];
    if (error) {
      console.warn('fetchCompanyMemberCompliance error:', error);
      continue;
    }
    for (const row of data || []) {
      // requirement_id query is index 0 when both exist, so it processes last and wins
      if (i === 0 || !map.has(row.user_id)) {
        map.set(row.user_id, {
          status: row.status,
          expiry_date: row.expiry_date,
          renewal_status: row.renewal_status,
          quote_id: row.quote_id
        });
      }
    }
  }

  return map;
}

/**
 * Classifies a member's compliance record with detailed information for prioritization.
 * Priority order:
 *   0: Expired (assigned, was qualified but expired) - needs renewal urgently
 *   1: Missing (assigned, never qualified) - needs initial training
 *   2.x: Expiring (assigned, expires within 90 days) - plan ahead
 *   3: Not assigned (not assigned to requirement AND no valid qualification) - should be trained
 *   100: Valid or Booked - no action needed
 *
 * @param {Object|null} record - { status, expiry_date, hasAssignment?, hasQualification? }
 * @returns {Object|null} { status, priority, expiryDate, daysUntilExpiry, description }
 */
export function classifyMemberCompliance(record) {
  // No record at all means we have no data for this user
  if (!record) return null;

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const { hasAssignment, hasQualification, status, expiry_date } = record;

  // Check if booked
  if (status === 'booked' || record.renewal_status === 'booked') {
    return {
      status: 'booked',
      priority: 100,
      expiryDate: expiry_date ? new Date(expiry_date) : null,
      daysUntilExpiry: null,
      description: 'Already booked'
    };
  }

  // Check completed qualifications (either from assignment or direct qualification)
  if (status === 'completed') {
    if (!expiry_date) {
      return {
        status: 'valid',
        priority: 100,
        expiryDate: null,
        daysUntilExpiry: null,
        description: 'Valid (no expiry)'
      };
    }

    const expiry = new Date(expiry_date);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry.getTime() - now.getTime();
    const daysUntilExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      // Expired - highest priority
      return {
        status: 'expired',
        priority: 0 + (Math.min(Math.abs(daysUntilExpiry), 365) / 1000), // 0.000 to 0.365 based on days ago
        expiryDate: expiry,
        daysUntilExpiry: daysUntilExpiry,
        description: daysUntilExpiry === -1
          ? 'Expired yesterday'
          : `Expired ${Math.abs(daysUntilExpiry)} days ago`
      };
    }

    if (daysUntilExpiry <= 90) {
      // Expiring soon
      return {
        status: 'expiring',
        priority: 2 + (daysUntilExpiry / 100), // 2.00 to 2.90 based on days
        expiryDate: expiry,
        daysUntilExpiry: daysUntilExpiry,
        description: daysUntilExpiry === 0
          ? 'Expires today'
          : daysUntilExpiry === 1
          ? 'Expires tomorrow'
          : `Expires in ${daysUntilExpiry} days`
      };
    }

    return {
      status: 'valid',
      priority: 100,
      expiryDate: expiry,
      daysUntilExpiry: daysUntilExpiry,
      description: 'Valid'
    };
  }

  // Has assignment but no completed qualification = missing/needs training
  if (hasAssignment) {
    return {
      status: 'missing',
      priority: 1,
      expiryDate: null,
      daysUntilExpiry: null,
      description: 'No certificate'
    };
  }

  // NOT assigned AND no valid qualification = should be recommended for training
  if (!hasAssignment && !hasQualification) {
    return {
      status: 'not_assigned',
      priority: 3,
      expiryDate: null,
      daysUntilExpiry: null,
      description: 'Not assigned'
    };
  }

  // Has a valid qualification but not assigned - they're fine
  if (hasQualification) {
    return {
      status: 'valid',
      priority: 100,
      expiryDate: null,
      daysUntilExpiry: null,
      description: 'Valid'
    };
  }

  return null;
}
